import { Add } from '@mui/icons-material';
import {
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { checkSIIDocumentForUploadingInvoices } from 'lib/api/sii';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import {
  formatDateBack,
  formatMoney,
  formatMoneyBack,
  showAlert,
  validaRut,
} from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { NewDocument } from '..';

interface AddDocumentsFormProps {
  documents: NewDocument[];
  setDocuments: (documents: NewDocument[]) => void;
  stakeholderIdentifier: string;
}

const AddDocumentsForm = (props: AddDocumentsFormProps) => {
  const { documents, setDocuments, stakeholderIdentifier } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [newDocument, setNewDocument] = useState<NewDocument>({
    folio: '',
    businessIdentifier: '',
    businessName: '',
    stakeholderIdentifier: stakeholderIdentifier,
    issuedDate: '',
    amount: 0,
    subType: '33',
  });

  const validateRuts = () => {
    if (validaRut(newDocument.businessIdentifier)) {
      return true;
    }
    return false;
  };

  const checkAllFields = () => {
    if (
      newDocument.folio === '' ||
      newDocument.businessIdentifier === '' ||
      newDocument.businessName === '' ||
      newDocument.stakeholderIdentifier === '' ||
      newDocument.amount === 0 ||
      newDocument.subType === ''
    ) {
      return false;
    }
    return true;
  };

  const handleChangeDate = (date: string) => {
    const [year, month, day] = date.split('-');
    setNewDocument({ ...newDocument, issuedDate: `${day}${month}${year}` });
  };

  const handleAddDocument = async () => {
    setLoading(true);
    if (!checkAllFields()) {
      showAlert(dispatch, 'error', 'Debes llenar todos los campos');
      return setLoading(false);
    }
    if (!validateRuts()) {
      showAlert(dispatch, 'error', 'RUT inválido');
      return setLoading(false);
    }
    try {
      const response = await checkSIIDocumentForUploadingInvoices(dispatch, {
        invoices: [
          {
            ...newDocument,
            type:
              newDocument.subType === '33'
                ? 'electronic-invoice'
                : newDocument.subType === '34'
                  ? 'electronic-invoice-exempt'
                  : 'electronic-invoice-buyer',
          },
        ],
      });
      if (response.length) {
        const responseDocument = response[0];
        const valid = responseDocument.valid;
        const message = responseDocument.message;
        if (valid) {
          newDocument.issuedDate = formatDateBack(newDocument.issuedDate);
          setDocuments([...documents, newDocument]);
          setNewDocument({
            folio: '',
            businessIdentifier: '',
            businessName: '',
            stakeholderIdentifier: stakeholderIdentifier,
            issuedDate: '',
            amount: 0,
            subType: '33',
          });
          return setLoading(false);
        } else {
          showAlert(
            dispatch,
            'error',
            message ||
              'La factura no es válida. El documento no ha sido recibido.',
          );
          return setLoading(false);
        }
      } else {
        showAlert(dispatch, 'error', 'Error al verificar el documento');
        return setLoading(false);
      }
    } catch (error) {
      console.error(error);
      showAlert(dispatch, 'error', 'Error al verificar el documento');
      return setLoading(false);
    }
  };

  if (loading) return <CenteredCircularProgress type="dialog" />;

  return (
    <>
      <Typography variant="body1">
        A continuación, ingresa los datos de los documentos que quieres
        ingresar.
      </Typography>
      <div style={{ padding: '20px 0px' }}>
        <Grid container spacing={2} alignItems={'center'}>
          <Grid item xs={8 / 5}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Tipo</InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label="Tipo"
                value={newDocument.subType}
                onChange={(e) =>
                  setNewDocument({
                    ...newDocument,
                    subType: String(e.target.value),
                  })
                }
              >
                <MenuItem value="33">Factura electrónica</MenuItem>
                <MenuItem value="34">Factura exenta</MenuItem>
                <MenuItem value="46">Factura de compra</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={8 / 5}>
            <TextField
              fullWidth
              label="Folio"
              value={newDocument.folio}
              onChange={(e) =>
                setNewDocument({ ...newDocument, folio: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={8 / 5}>
            <TextField
              fullWidth
              label="RUT proveedor *"
              value={newDocument.businessIdentifier}
              onChange={(e) =>
                setNewDocument({
                  ...newDocument,
                  businessIdentifier: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={8 / 5}>
            <TextField
              fullWidth
              label="Nombre proveedor"
              value={newDocument.businessName}
              onChange={(e) =>
                setNewDocument({
                  ...newDocument,
                  businessName: e.target.value,
                })
              }
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="Fecha de Emisión"
              type="date"
              value={formatDateBack(newDocument.issuedDate)}
              onChange={(e) => handleChangeDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={8 / 5}>
            <TextField
              fullWidth
              label="Monto"
              value={formatMoney(newDocument.amount)}
              onChange={(e) =>
                setNewDocument({
                  ...newDocument,
                  amount: formatMoneyBack(e.target.value),
                })
              }
            />
          </Grid>
          <Grid item xs={1}>
            {loading ? (
              <CircularProgress size={24} />
            ) : (
              <IconButton
                color="primary"
                onClick={() => {
                  void handleAddDocument();
                }}
              >
                <Add />
              </IconButton>
            )}
          </Grid>
        </Grid>
        <Typography variant="caption">* Rut sin puntos y con guión</Typography>
      </div>
    </>
  );
};

export default AddDocumentsForm;
